/**
 * RecurrenceLogbookSection — collapsible section showing recurring task history
 * grouped by template, with streak indicators.
 *
 * Designed for the Logbook page's `beforeContent` slot.
 */

import { memo, useState, useMemo } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { queryRecurrenceLogbook } from "@/db/operations"
import { ObjectStatus } from "@totodo/operations"
import { cn } from "@/shared/utils/general/cn"
import { useTranslation } from "react-i18next"
import { ChevronRight, Repeat, Flame } from "lucide-react"
import dayjs from "dayjs"
import { parseDbDate } from "@totodo/operations"

export const RecurrenceLogbookSection = memo(function RecurrenceLogbookSection() {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const [groups] = useLiveQueryImmediate(
    () => queryRecurrenceLogbook(20),
    ["recurrenceLogbook"],
    [],
  )

  // Only show if there are recurring templates with completed instances
  if (!groups || groups.length === 0) return null

  return (
    <div className="mb-4 px-3">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full cursor-pointer items-center gap-1.5 py-2 text-left"
      >
        <ChevronRight
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            expanded && "rotate-90",
          )}
        />
        <Repeat className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold text-foreground">
          {t("Recurring History")}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {groups.length}
        </span>
      </button>

      {/* Grouped list */}
      {expanded && (
        <div className="mt-1 space-y-3 pb-3">
          {groups.map((group) => (
            <RecurrenceGroup key={group.templateId} group={group} />
          ))}
        </div>
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Single template group
// ---------------------------------------------------------------------------

interface RecurrenceGroupProps {
  group: {
    templateId: string
    templateTitle: string
    recurrence: { currentStreak?: number; longestStreak?: number }
    instances: Array<{
      id: string
      title: string
      occurrenceKey: string
      status: number
      statusAt?: string
      userStatus?: number | string
    }>
  }
}

const RecurrenceGroup = memo(function RecurrenceGroup({ group }: RecurrenceGroupProps) {
  const [showAll, setShowAll] = useState(false)
  const displayed = showAll ? group.instances : group.instances.slice(0, 5)
  const hasMore = group.instances.length > 5

  return (
    <div className="rounded-lg border border-border/50 bg-card/50 px-3 py-2">
      {/* Template header */}
      <div className="flex items-center gap-2">
        <Repeat className="h-3.5 w-3.5 shrink-0 text-primary/70" />
        <span className="truncate text-sm font-medium text-foreground">
          {group.templateTitle}
        </span>
        {(group.recurrence.currentStreak ?? 0) > 0 && (
          <span className="ml-auto flex items-center gap-0.5 text-xs text-orange-500">
            <Flame className="h-3 w-3" />
            {group.recurrence.currentStreak}
          </span>
        )}
      </div>

      {/* Instance list */}
      <div className="mt-1.5 space-y-0.5">
        {displayed.map((inst) => (
          <InstanceRow key={inst.id} instance={inst} />
        ))}
      </div>

      {/* Show more */}
      {hasMore && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1 cursor-pointer text-xs text-primary hover:underline"
        >
          +{group.instances.length - 5} more
        </button>
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Single instance row
// ---------------------------------------------------------------------------

interface InstanceRowProps {
  instance: {
    id: string
    title: string
    occurrenceKey: string
    status: number
    statusAt?: string
    userStatus?: number | string
  }
}

const InstanceRow = memo(function InstanceRow({ instance }: InstanceRowProps) {
  const isSkipped = instance.userStatus === "skipped" || instance.status === ObjectStatus.Canceled
  const dateLabel = useMemo(() => {
    const statusMs = parseDbDate(instance.statusAt)
    if (statusMs !== undefined) {
      return dayjs(statusMs).format("MMM D")
    }
    // Fallback: parse occurrenceKey (YYYYMMDD)
    if (instance.occurrenceKey && instance.occurrenceKey.length === 8) {
      return dayjs(instance.occurrenceKey, "YYYYMMDD").format("MMM D")
    }
    return ""
  }, [instance.statusAt, instance.occurrenceKey])

  return (
    <div className="flex items-center gap-2 py-0.5 text-xs">
      <span
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          isSkipped ? "bg-muted-foreground/40" : "bg-green-500",
        )}
      />
      <span
        className={cn(
          "truncate",
          isSkipped ? "text-muted-foreground line-through" : "text-foreground/80",
        )}
      >
        {instance.title}
      </span>
      <span className="ml-auto shrink-0 text-muted-foreground">
        {dateLabel}
      </span>
    </div>
  )
})
